import { useState, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  Legend, LabelList, LineChart, Line,
} from "recharts";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { storeName } from "../lib/labels";

const MEASURES = [
  {
    key: "revenue", label: "Revenue",
    fmt: (v) => `$${Math.round(v).toLocaleString()}`,
    short: (v) => `$${(v / 1000).toFixed(1)}k`,
    priorField: "prior_revenue",
    growthPrior: "growth_pct_revenue",
    yoyField: "yoy_revenue",
    growthYoy: "yoy_growth_pct_revenue",
    yoyDatesField: "yoy_dates_revenue",
    growthYoyDates: "yoy_dates_growth_pct_revenue",
  },
  {
    key: "transactions", label: "Transactions",
    fmt: (v) => Math.round(v).toLocaleString(),
    short: (v) => Math.round(v).toLocaleString(),
    priorField: "prior_transactions",
    growthPrior: "growth_pct_transactions",
    yoyField: "yoy_transactions",
    growthYoy: "yoy_growth_pct_transactions",
    yoyDatesField: "yoy_dates_transactions",
    growthYoyDates: "yoy_dates_growth_pct_transactions",
  },
  {
    key: "units", label: "Units",
    fmt: (v) => Math.round(v).toLocaleString(),
    short: (v) => Math.round(v).toLocaleString(),
    priorField: "prior_units",
    growthPrior: null,
    yoyField: "yoy_units",
    growthYoy: null,
    yoyDatesField: "yoy_dates_units",
    growthYoyDates: null,
  },
];

const STORE_ORDER = ["Public Butter Kensington", "Black Market 347", "Black Market 256", "Dirty Mike's"];
const BUCKET_COLORS = ["#0a0a0a", "#c72c2c", "#6b6b6b", "#a37e2c", "#2c6b64", "#7ba43a", "#3a6b9a", "#8e5cd9"];
const GHOST_COLORS = ["#0a0a0a55", "#c72c2c55", "#6b6b6b55", "#a37e2c55", "#2c6b6455", "#7ba43a55", "#3a6b9a55", "#8e5cd955"];

// Convert bucket key ("2026-08", "2026-W35", "2026-09-08") to a short label
function bucketLabel(b) {
  if (!b) return "";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  if (b.length === 7) return months[parseInt(b.slice(5, 7), 10) - 1];
  if (b.includes("-W")) return b.split("-W")[1];
  if (b.length === 10) return `${months[parseInt(b.slice(5,7),10)-1]} ${parseInt(b.slice(8,10),10)}`;
  return b;
}

// Single-character / short label to render UNDER each bar as a mini x-axis:
// months → J F M A M J J A S O N D, weeks → "W35", days → day number.
function bucketTick(b) {
  if (!b) return "";
  if (b.length === 7) {
    const m = parseInt(b.slice(5, 7), 10);
    return ["J","F","M","A","M","J","J","A","S","O","N","D"][m - 1] || "";
  }
  if (b.includes("-W")) return `W${b.split("-W")[1]}`;
  if (b.length === 10) return String(parseInt(b.slice(8, 10), 10));
  return b;
}

// Current ISO week id ("2026-W37") from the wall clock — used to drop the
// still-in-progress week from the 12-week momentum sparkline.
function currentISOWeekId() {
  const now = new Date();
  const target = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dayNum = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const wk = 1 + Math.round(((target - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return `${target.getUTCFullYear()}-W${String(wk).padStart(2, "0")}`;
}

// Tiny inline sparkline (no axes, no ticks) — takes an array of numbers.
function Sparkline({ points, color = "#0a0a0a" }) {
  if (!points || points.length < 2) return null;
  const data = points.map((v, i) => ({ i, v }));
  return (
    <div className="h-6 w-24">
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.4} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Custom Bar shape that draws the ghost (prior/YoY) directly behind the current
// bar with a slight down-right offset — the classic "drop-shadow" style.
function makeBucketBar(bucketKey, fill, ghostFill) {
  return (props) => {
    const { x, y, width, height, payload } = props;
    if (payload == null || width <= 0) return null;
    const currentValue = payload[bucketKey] || 0;
    const ghostValue = payload[`${bucketKey}__ghost`] || 0;
    const OFFSET = 5;
    let ghostRect = null;
    if (ghostValue > 0 && currentValue > 0) {
      const pxPerUnit = height / currentValue;
      const ghostHeight = ghostValue * pxPerUnit;
      const ghostBottom = y + height;
      const ghostTop = ghostBottom - ghostHeight;
      ghostRect = (
        <rect
          x={x + OFFSET}
          y={ghostTop + OFFSET}
          width={width}
          height={Math.max(0, ghostHeight - OFFSET)}
          fill={ghostFill}
        />
      );
    } else if (ghostValue > 0 && currentValue === 0) {
      // Rare: no current sales but prior exists — still show the ghost at a
      // reasonable position (offset just below y-axis 0)
      ghostRect = (
        <rect
          x={x + OFFSET}
          y={y + height - 4}
          width={width}
          height={2}
          fill={ghostFill}
        />
      );
    }
    return (
      <g>
        {ghostRect}
        <rect x={x} y={y} width={width} height={height} fill={fill} />
      </g>
    );
  };
}

export default function OutletComparison({ data, trend, sparkTrend, rangeLabel, compareMode = "same_period", loading = false }) {
  const [mKey, setMKey] = useState("revenue");
  const measure = MEASURES.find((m) => m.key === mKey);

  const showGhost = compareMode === "same_period" || compareMode === "same_dates";
  // Backend now serves the trend YoY series aligned to whichever compare mode
  // is active — front-end just reads the `_yoy` suffix. Row totals use the
  // matching precise field on `by_outlet` so numbers agree with the header.
  const ghostSuffix = "_yoy";
  const rowGhostField = compareMode === "same_dates" ? measure.yoyDatesField : measure.yoyField;
  const rowGrowthField = compareMode === "same_dates" ? measure.growthYoyDates : measure.growthYoy;

  // Reshape trend rows (bucket + per-outlet-name) into rows keyed by store.
  const clusterData = useMemo(() => {
    if (!trend?.length || !data?.length) return { rows: [], buckets: [] };
    const buckets = trend.map((t) => t.bucket);
    const byNameOutlets = Object.fromEntries((data || []).map((r) => [r.name, r]));
    const ordered = STORE_ORDER.filter((n) => byNameOutlets[n]).map((n) => byNameOutlets[n])
      .concat((data || []).filter((r) => !STORE_ORDER.includes(r.name)));
    const rows = ordered.map((o) => {
      const row = { name: storeName(o.name), fullName: o.name };
      let total = 0;
      for (const t of trend) {
        const v = t[o.name] ?? 0;
        row[t.bucket] = v;
        row[`${t.bucket}__ghost`] = showGhost ? (t[`${o.name}${ghostSuffix}`] ?? 0) : 0;
        total += v;
      }
      row.__total = mKey === "revenue" ? total : (o[mKey] ?? 0);
      row.__ghostTotal = showGhost ? (o[rowGhostField] ?? 0) : null;
      row.__growth = showGhost ? o[rowGrowthField] : null;
      return row;
    });
    return { rows, buckets };
  }, [trend, data, mKey, showGhost, measure, rowGhostField, rowGrowthField]);

  // Sparkline data by outlet name → last 12 COMPLETE weekly values
  // (the current in-progress week is dropped so momentum reflects finished weeks)
  const sparkByStore = useMemo(() => {
    const out = {};
    if (!sparkTrend?.length) return out;
    const curWeek = currentISOWeekId();
    for (const row of sparkTrend) {
      if (row.bucket === curWeek) continue;
      for (const [k, v] of Object.entries(row)) {
        if (k === "bucket" || typeof v !== "number") continue;
        (out[k] ||= []).push(v);
      }
    }
    return out;
  }, [sparkTrend]);

  if (!data?.length) {
    if (loading) {
      return (
        <div className="border border-[#0a0a0a] bg-[#f5f2eb] p-10 text-center animate-pulse" data-testid="outlet-comparison-loading">
          <div className="bm-eyebrow text-neutral-600">Loading store-vs-store…</div>
        </div>
      );
    }
    return null;
  }

  const compareLabel = compareMode === "none"
    ? null
    : compareMode === "same_dates"
      ? "vs. same dates last year"
      : "vs. same period last year";
  const ghostShortLabel = compareMode === "same_dates" ? "LY dates" : "LY";

  return (
    <div className="border border-[#0a0a0a] bg-[#f5f2eb] p-5" data-testid="outlet-comparison">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold">Store-vs-store</h3>
          {rangeLabel && (
            <div className="bm-eyebrow text-neutral-500 mt-1">
              {rangeLabel}{compareLabel ? ` · ${compareLabel}` : ""} · from the date picker
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex flex-wrap" data-testid="measure-selector">
            {MEASURES.map((m) => (
              <button
                key={m.key}
                onClick={() => setMKey(m.key)}
                data-testid={`measure-${m.key}`}
                disabled={m.key !== "revenue"}
                title={m.key !== "revenue" ? "Revenue trend only (per-bucket other measures not yet available)" : ""}
                className={`px-2 py-1 bm-eyebrow border border-[#0a0a0a] -ml-px first:ml-0 ${
                  mKey === m.key ? "bg-[#0a0a0a] text-[#f5f2eb]" : "hover:bg-[#0a0a0a]/10"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={clusterData.rows} margin={{ top: 24, right: 20, left: 0, bottom: 0 }} barGap={6} barCategoryGap="8%">
          <CartesianGrid strokeDasharray="2 4" stroke="#0a0a0a20" />
          <XAxis dataKey="name" stroke="#0a0a0a" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
          <YAxis stroke="#0a0a0a" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickFormatter={measure.short} />
          <Tooltip
            contentStyle={{ background: "#f5f2eb", border: "1px solid #0a0a0a", color: "#0a0a0a", fontSize: 12, fontFamily: "JetBrains Mono" }}
            formatter={(v, name) => [measure.fmt(v), bucketLabel(name)]}
            labelFormatter={(label, entries) => {
              const bucket = entries?.[0]?.dataKey;
              const item = entries?.[0]?.payload;
              const ghostV = showGhost ? item?.[`${bucket}__ghost`] : null;
              const ghostLine = ghostV ? ` · ${ghostShortLabel} ${measure.fmt(ghostV)}` : "";
              return `${label}${ghostLine}`;
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: 10, fontFamily: "JetBrains Mono", textTransform: "uppercase", letterSpacing: "0.15em" }}
            formatter={(name) => bucketLabel(name)}
          />
          {clusterData.buckets.map((b, i) => {
            const fill = BUCKET_COLORS[i % BUCKET_COLORS.length];
            const ghostFill = GHOST_COLORS[i % GHOST_COLORS.length];
            const tick = bucketTick(b);
            return (
              <Bar
                key={b}
                dataKey={b}
                fill={fill}
                shape={makeBucketBar(b, fill, ghostFill)}
                isAnimationActive={false}
              >
                {/* Value on top */}
                <LabelList
                  dataKey={b}
                  position="top"
                  formatter={measure.short}
                  style={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: "#0a0a0a", fontWeight: 500 }}
                />
                {/* Bucket letter inside the bottom of each bar — proves the
                    order at a glance without hunting through the legend. */}
                <LabelList
                  dataKey={b}
                  position="insideBottom"
                  content={(props) => {
                    const { x, y, width, height } = props;
                    if (!width || width < 8 || !height) return null;
                    // Keep the letter INSIDE the bar so it never collides with
                    // the store's x-axis tick underneath. If the bar is very
                    // short, drop the label rather than clipping.
                    if (height < 14) return null;
                    return (
                      <text
                        x={x + width / 2}
                        y={y + height - 5}
                        textAnchor="middle"
                        style={{
                          fontFamily: "JetBrains Mono",
                          fontSize: 10,
                          fontWeight: 700,
                          fill: "#f5f2eb",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {tick}
                      </text>
                    );
                  }}
                />
              </Bar>
            );
          })}
        </BarChart>
      </ResponsiveContainer>

      <div className="mt-4 divide-y divide-[#0a0a0a]/20">
        {clusterData.rows.map((r, i) => {
          const g = r.__growth;
          const gDefined = g !== null && g !== undefined;
          const up = gDefined && g >= 0;
          const Icon = !gDefined ? Minus : up ? ArrowUpRight : ArrowDownRight;
          const gColor = !gDefined ? "text-neutral-400" : up ? "text-emerald-700" : "text-[#c72c2c]";
          const sparkPoints = sparkByStore[r.fullName] || [];
          const sparkColor = sparkPoints.length > 1 && sparkPoints[sparkPoints.length - 1] >= sparkPoints[0]
            ? "#0a7a4a"
            : "#c72c2c";
          return (
            <div key={r.fullName} className="py-3 md:py-2 flex flex-wrap md:flex-nowrap items-center justify-between text-sm gap-x-4 gap-y-2" data-testid={`outlet-row-${r.name}`}>
              <div className="flex items-center gap-3 min-w-0 order-1">
                <span className="bm-eyebrow w-6 text-neutral-500">{i + 1}</span>
                <span className="min-w-[80px] font-medium md:font-normal">{r.name}</span>
                <span className="hidden md:inline-flex items-center gap-3">
                  <Sparkline points={sparkPoints} color={sparkColor} />
                  <span className="bm-eyebrow text-neutral-400">12-wk momentum</span>
                </span>
              </div>
              <div className="flex items-baseline gap-4 order-2 md:order-3">
                {showGhost && (
                  <div
                    className={`font-mono-bm tabular-nums text-[11px] font-semibold inline-flex items-center gap-0.5 ${gColor}`}
                    data-testid={`outlet-growth-${r.name}`}
                  >
                    <Icon size={11} strokeWidth={2.5} />
                    {gDefined ? `${up ? "+" : ""}${g.toFixed(1)}%` : "—"}
                  </div>
                )}
                <div className="text-right">
                  <div className="font-mono-bm text-neutral-800 tabular-nums font-semibold">
                    {measure.fmt(r.__total)}
                  </div>
                  {showGhost && (
                    <div className="font-mono-bm text-neutral-500 tabular-nums text-[10px]">
                      {ghostShortLabel} {measure.fmt(r.__ghostTotal || 0)}
                    </div>
                  )}
                </div>
              </div>
              <div className="md:hidden w-full pl-9 flex items-center gap-3 order-3">
                <Sparkline points={sparkPoints} color={sparkColor} />
                <span className="bm-eyebrow text-neutral-400">12-wk momentum</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
